import { appMode } from '@/utils/config/mode'
import { findDuplicates } from '@/utils/helpers/findDuplicates'
import _ from '@/utils/setup/lodash'
import account from './account'
import alerts from './alerts'
import apiErrors from './api-errors'
import cities from './cities'
import cloudAccounts from './cloud-accounts'
import cloudCostManagement from './cloud-cost-management'
import credentials from './credentials'
import environment from './environment'
import errors from './errors'
import events from './events'
import externalBackends from './external-backends'
import filters from './filters'
import forms from './forms'
import infra from './infra'
import inventory from './inventory'
import kpis from './kpis'
import lang from './lang'
import legal from './legal'
import licence from './licence'
import members from './members'
import pipeline from './pipeline'
import plans from './plans'
import plugins from './plugins'
import pluralizations from './pluralizations'
import presentation from './presentation'
import roles from './roles'
import routes from './routes'
import severities from './severities'
import sorts from './sorts'
import stacks from './stacks'
import terracost from './terracost'
import time from './time'
import watchRules from './watchRules'
import workers from './workers'

const commonCycloidTerms = {
  Activity: 'Activity',
  APIKey: 'API key',
  APIKeys: 'API keys',
  Appearance: 'Appearance',
  CatalogRepositories: 'Catalog repositories',
  CatalogRepository: 'Catalog repository',
  Component: 'Component',
  ConfigRepositories: 'Config repositories',
  ConfigRepository: 'Config repository',
  Credential: 'Credential',
  Credentials: 'Credentials',
  Dashboard: 'Dashboard',
  EnvComponent: 'Component',
  EnvComponents: 'Components',
  CloudAccounts: 'Cloud Accounts',
  Environment: 'Environment',
  Environments: 'Environments',
  Events: 'Events',
  InfraPolicy: 'InfraPolicy',
  InfraPolicies: 'InfraPolicies',
  Infrastructure: 'Infrastructure',
  Inventory: 'Inventory',
  Member: 'Member',
  Members: 'Members',
  Metrics: 'Metrics',
  Organization: 'Organization',
  Organizations: 'Organizations',
  Pending: 'Pending',
  Project: 'Project',
  Projects: 'Projects',
  Resource: 'Resource',
  Resources: 'Resources',
  Role: 'Role',
  Roles: 'Roles',
  Stack: 'Stack',
  Stacks: 'Stacks',
  Team: 'Team',
  Teams: 'Teams',
  TerraformBackend: 'Terraform backend',
  Users: 'Users',
  Output: 'Output',
  Outputs: 'Outputs',
}

// Add the same set of terms with camelCased keys and lowercased values.
const camelCaseTerms = _.mapValues(_.$camelCaseKeys(commonCycloidTerms), _.toLower)
Object.assign(commonCycloidTerms, camelCaseTerms)

const en = {
  all: 'All',
  about: 'About',
  and: 'and',
  assignOwner: 'Assign owner',
  Author: 'Author',
  clone: 'Clone',
  color: 'Color',
  collapse: 'collapse',
  copy: 'Copy',
  copyToClipboard: 'Copy to clipboard',
  comingSoon: 'coming soon',
  comparedToLastWeek: 'Compared to last week',
  configuration: 'Configuration',
  configure: 'Configure',
  date: 'Date',
  Default: 'Default',
  details: 'Details',
  Details: 'Details',
  development: 'development',
  disabled: 'Disabled',
  editor: 'Editor',
  enabled: 'Enabled',
  Filters: 'Filters',
  from: 'from',
  fullScreen: 'Full screen',
  general: 'General',
  icon: 'Icon',
  importing: 'importing',
  invite: 'Invite',
  learnMore: 'Learn more',
  maintainer: 'Maintainer',
  name: 'Name',
  noOwner: 'no owner',
  or: 'or',
  other: 'Other',
  others: 'Others',
  owner: 'Owner',
  Pending: 'Pending',
  preview: 'Preview',
  properties: 'Properties',
  provider: 'Provider',
  ReadMore: 'Read more',
  seeMore: 'see more',
  Settings: 'Settings',
  Summary: 'Summary',
  weekOf: 'Week of',
  welcomeMsg: 'Welcome to Cycloid App',
  youAreUsing: 'You are using',
  youDoNotHavePermissionsTo: 'You do not have sufficient permissions to',
  info: 'Info',
  warning: 'Warning',
  error: 'Error',
  critical: 'Critical',
  read: 'Read',
  unread: 'Unread',
  items: 'items',
  lastUsed: 'Last used',
  ...commonCycloidTerms,
  ...pluralizations,
}

// We flatten the imported objects in order to avoid sneaky clashes
// with previously defined strings (see FE#7160).
const moduleTranslations = {
  account: account.en,
  alerts: alerts.en,
  apiErrors: apiErrors.en,
  cities: cities.en,
  cloudAccounts: cloudAccounts.en,
  cloudCostManagement: cloudCostManagement.en,
  credentials: credentials.en,
  environment: environment.en,
  errors: errors.en,
  events: events.en,
  externalBackends: externalBackends.en,
  filters: filters.en,
  forms: forms.en,
  infra: infra.en,
  inventory: inventory.en,
  kpis: kpis.en,
  lang: lang.en,
  legal: legal.en,
  licence: licence.en,
  members: members.en,
  pipeline: pipeline.en,
  plans: plans.en,
  presentation: presentation.en,
  roles: roles.en,
  routes: routes.en,
  severities: severities.en,
  sorts: sorts.en,
  stacks: stacks.en,
  terracost: terracost.en,
  time: time.en,
  watchRules: watchRules.en,
  workers: workers.en,
  plugins: plugins.en,
}

// Final check for duplicate keys before exporting all translations (FE#7168).
const duplicates = findDuplicates([...Object.keys(en), ...Object.keys(moduleTranslations)])
if (duplicates.length && appMode.isDev) {
  console.warn(`Duplicate keys found in translations export: ${duplicates.join(', ')}`)
}

export default {
  ...en,
  ...moduleTranslations,
}
